import React from "react";
import { useSelector } from "react-redux";
import InfoTooltip from "src/components/tooltip";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip,
} from "recharts";

import cx from "classnames";
import styles from "./entityChartFilters.module.scss";
import dateformat from "dateformat";
import _ from "lodash";
import { Dropdown } from "react-bootstrap";
import { EntitiesFilterTooltip } from "../entitiesFilterTooltip";
import { DiscoveryTables, EngagementTables, PerformanceTables, ReportTabs } from "src/types/reports";

interface ChartDataItem extends Record<string, string | number> {
  key: string;
  value: number;
  cumulative: number;
}

interface FilterOption {
  label: string;
  value: string;
}

interface EntityChartFiltersProps {
  title: string;
  description?: string;
  data?: ChartDataItem[];
  legend?: any;
  valueFormatter?: (value: any) => string;
  containerClassName?: string;
  loading?: boolean;
  filterOptions?: FilterOption[];
  defaultFilter?: string;
  selectedFilter?: string;
  onFilterChange?: (filterValue: string) => void;
  tabName: ReportTabs;
  tableName: DiscoveryTables | EngagementTables | PerformanceTables;
}

const EntityChartFilters: React.FC<EntityChartFiltersProps> = ({
  title,
  description,
  data,
  legend,
  valueFormatter,
  containerClassName,
  loading,
  filterOptions,
  defaultFilter,
  selectedFilter,
  onFilterChange,
  tabName,
  tableName,
}) => {

  const primaryColor = useSelector((state: any) =>
    _.get(state.accounts, "item.theme.primaryColor", "#e51058"),
  );

  return (
    <div className={cx(styles.container, containerClassName)}>
      <div className={styles.card}>
        <div className={styles.report}>
          <div className={styles.filterOptions}>
              <div className={styles.filteredItem}>
            {filterOptions &&
              filterOptions.length > 0 &&
              selectedFilter &&
              onFilterChange && (
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    className={styles.dropdownButton}
                    as={"button"}
                  >
                    <div className={styles.filterList} />
                    {filterOptions.find((opt) => opt.value === selectedFilter)
                      ?.label || defaultFilter}
                  </Dropdown.Toggle>

                  <Dropdown.Menu className={styles.dropdownMenuContainer}>
                    {filterOptions.map((option) => (
                      <Dropdown.Item
                        key={option.value}
                        onClick={() => onFilterChange(option.value)}
                        className={cx(
                          styles.dropdownItem,
                          option.value === selectedFilter && styles.active,
                        )}
                      >
                        {option.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              </div>
              <EntitiesFilterTooltip tabName={tabName} tableName={tableName} />
          </div>
          <div className={styles.content}>
            <div className={styles.textContainer}>
              <div className={styles.title}>{title}</div>
              <InfoTooltip placement="top" content={description}>
                <i className={cx(styles.paperfliteInfo, "paperflite-info")}></i>
              </InfoTooltip>
            </div>
          </div>

          <div className={styles.chartContainer}>
          {loading && (
            <div className={styles.loading}>Loading…</div>
          )}
            {!loading && (
              <ResponsiveContainer width="100%" height={380}>
                <BarChart
                  data={data}
                  margin={{ top: 40, right: -20, bottom: 0, left: 0 }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="key"
                    axisLine={false}
                    tickFormatter={(tickItem) => dateformat(tickItem, "dd-mmm")}
                  />
                  <YAxis
                    dataKey="value"
                    axisLine={false}
                    orientation="right"
                    tickLine={false}
                    domain={[0, "dataMax"]}
                  />
                  <Tooltip
                    content={(props) => (
                      <CustomTooltip
                        {...props}
                        legend={legend}
                        valueFormatter={valueFormatter}
                      />
                    )}
                    cursor={false}
                  />
                  <Bar
                    dataKey="value"
                    fill={primaryColor || "#e51058"}
                    maxBarSize={10}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntityChartFilters;

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  legend: any;
  valueFormatter?: (value: any) => string;
}> = ({ active, payload, legend, valueFormatter }) => {
  if (active && payload && payload.length) {
    const payloadObj = _.get(payload, "[0].payload", null);
    if (!payloadObj) return null;

    let value = _.get(payloadObj, "value", "");
    if (valueFormatter) {
      value = valueFormatter(value);
    }

    return (
      <div className={styles.tooltip}>
        <p className="key">
          {`${legend.key} : ${dateformat(_.get(payloadObj, "key", ""), "dd-mmm-yyyy")}`}
        </p>
        <p className="value">{`${legend.value} : ${value}`}</p>
      </div>
    );
  }

  return null;
};